import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download, ExternalLink, BellRing, AlertTriangle, Wallet, Clock, CheckCircle2, Plus, FileMinus2, History, CreditCard, Mail, Eye, MailOpen, Send, Edit3, Beaker, Loader2, X } from "lucide-react";
import EmailHistoryPanel from "@/components/admin/EmailHistoryPanel";

function fmtEur(n) { return Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €"; }
function fmtShort(iso) {
  if (!iso) return "—";
  try { const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso); return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return iso; }
}
function daysFromToday(iso) {
  if (!iso) return null;
  try { const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso); const today = new Date(); today.setHours(0,0,0,0); return Math.floor((today.getTime() - d.getTime()) / 86400000); }
  catch { return null; }
}

const TYPE_LABEL = { acompte: "Acompte", solde: "Solde", complete: "Intégral", balance: "Solde", deposit: "Acompte", avoir: "Avoir" };
const PAID_METHODS = [
  { v: "virement", l: "Virement" },
  { v: "cb", l: "Carte bancaire" },
  { v: "cheque", l: "Chèque" },
  { v: "especes", l: "Espèces" },
  { v: "autre", l: "Autre" },
];

export default function AdminInvoices() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [reminding, setReminding] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showAcompte, setShowAcompte] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creditNoteFor, setCreditNoteFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [sendModal, setSendModal] = useState(null); // { inv, mode: 'send'|'remind' }

  const load = () => api.get("/admin/invoices").then((r) => {
    setItems(r.data);
    setActive((prev) => prev ? r.data.find((i) => i.id === prev.id) || r.data[0] || null : r.data[0] || null);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!active?.id) { setPreviewUrl(null); return; }
    let revoke = null;
    const url = `${window.location.origin}/api/admin/invoices/${active.id}/pdf`;
    const tok = localStorage.getItem("besson_admin_token");
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => r.ok ? r.blob() : null)
      .then((b) => { if (!b) { setPreviewUrl(null); return; } const u = URL.createObjectURL(b); revoke = u; setPreviewUrl(u); })
      .catch(() => setPreviewUrl(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [active]);

  const dl = (id, ref) => {
    const url = `${window.location.origin}/api/admin/invoices/${id}/pdf`;
    const tok = localStorage.getItem("besson_admin_token");
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => r.blob())
      .then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `${ref}.pdf`; a.click(); URL.revokeObjectURL(u); });
  };

  const sendReminder = (inv) => setSendModal({ inv, mode: "remind" });
  const sendInvoice = (inv) => setSendModal({ inv, mode: "send" });

  const stats = useMemo(() => {
    const unpaid = items.filter((i) => !i.paid && !i.is_credit_note);
    const totalUnpaid = unpaid.reduce((s, i) => s + Number(i.ttc || 0), 0);
    const overdue = unpaid.filter((i) => i.due_at && (daysFromToday(i.due_at) || 0) > 0);
    const totalOverdue = overdue.reduce((s, i) => s + Number(i.ttc || 0), 0);
    const paidCount = items.filter((i) => i.paid && !i.is_credit_note).length;
    return { unpaidCount: unpaid.length, totalUnpaid, overdueCount: overdue.length, totalOverdue, paidCount };
  }, [items]);

  return (
    <div data-testid="admin-invoices">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Factures</h1>
          <p className="text-sm text-[#64748B] mt-1">Création manuelle, avoirs, relances, paiements.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAcompte(true)} variant="outline" className="border-[#0A1128] text-[#0A1128] hover:bg-[#0A1128] hover:text-white font-bold" data-testid="invoice-acompte-btn">
            <FileMinus2 size={16} className="mr-1.5" />Acompte sur devis
          </Button>
          <Button onClick={() => setShowCreate(true)} className="bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-bold" data-testid="invoice-new-btn">
            <Plus size={16} className="mr-1.5" />Nouvelle facture
          </Button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="inv-stats">
        <MiniStat tone="navy" icon={Wallet} label="En attente" value={fmtEur(stats.totalUnpaid)} sub={`${stats.unpaidCount} facture(s)`} />
        <MiniStat tone="red" icon={AlertTriangle} label="En retard" value={fmtEur(stats.totalOverdue)} sub={`${stats.overdueCount} dépassée(s)`} />
        <MiniStat tone="green" icon={CheckCircle2} label="Payées" value={String(stats.paidCount)} sub={`${items.length} totales`} />
        <MiniStat tone="cyan" icon={Clock} label="Cycle moyen" value="—" sub="J. entre envoi & paiement" />
      </div>

      <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-black/8 rounded-2xl overflow-hidden lg:max-h-[calc(100vh-340px)] lg:overflow-y-auto">
          {items.length === 0 ? (
            <div className="text-sm text-[#64748B] p-8 text-center">Aucune facture. Cliquez "Nouvelle facture" pour démarrer.</div>
          ) : (
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead className="bg-[#FAFAFA] border-b border-black/8 text-[10px] font-bold tracking-widest uppercase text-[#64748B] sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2.5">N° / Type</th>
                  <th className="text-left px-3 py-2.5">Client</th>
                  <th className="text-right px-3 py-2.5">TTC</th>
                  <th className="text-left px-3 py-2.5">Échéance</th>
                  <th className="text-left px-3 py-2.5">Statut</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => {
                  const isActive = active?.id === i.id;
                  const overdueDays = !i.paid && i.due_at ? daysFromToday(i.due_at) : null;
                  const isOverdue = overdueDays != null && overdueDays > 0;
                  const isCN = !!i.is_credit_note;
                  return (
                    <tr key={i.id} onClick={() => setActive(i)} data-testid={`invoice-${i.id}`} className={`border-b border-black/5 last:border-0 cursor-pointer transition-colors ${isActive ? "bg-[#06B6D4]/8" : "hover:bg-[#FAFAFA]"}`}>
                      <td className="px-3 py-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditing(i); }}
                          data-testid={`invoice-ref-link-${i.id}`}
                          className={`display font-extrabold hover:underline text-left ${isCN ? "text-red-600" : "text-[#0A1128]"}`}
                        >{i.ref}</button>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0">{TYPE_LABEL[i.type] || i.type}</Badge>
                          {i.proposal_id && <Link to={`/admin/devis/${i.proposal_id}`} onClick={(e) => e.stopPropagation()} className="text-[10px] text-[#06B6D4] hover:underline">→ {i.proposal_ref}</Link>}
                          {i.parent_invoice_ref && <span className="text-[10px] text-[#64748B]">↳ {i.parent_invoice_ref}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-[#475569] truncate max-w-[160px]">
                        <div className="font-medium text-[#0A1128]">{i.client_name || "—"}</div>
                        <div className="text-[10px] text-[#64748B]">{i.client_email || ""}</div>
                      </td>
                      <td className={`px-3 py-3 text-right font-extrabold tabular-nums ${isCN ? "text-red-600" : "text-[#0A1128]"}`}>{Number(i.ttc).toLocaleString("fr-FR", { minimumFractionDigits: 0 })} €</td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap">
                        <div className="text-[#475569]">{fmtShort(i.due_at)}</div>
                        {isOverdue && (
                          <div className="text-[10px] font-bold text-red-600 inline-flex items-center gap-0.5 mt-0.5">
                            <AlertTriangle size={9} />+{overdueDays} j de retard
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {i.paid ? (
                          <div>
                            <Badge className="bg-green-600 text-[10px] font-bold">Payée</Badge>
                            <div className="text-[10px] text-[#065F46] mt-0.5 leading-tight">
                              {i.paid_amount ? `${Number(i.paid_amount).toLocaleString("fr-FR")} € · ` : ""}{fmtShort(i.paid_at)}
                              {i.paid_method ? ` · ${(PAID_METHODS.find((m) => m.v === i.paid_method) || {}).l || i.paid_method}` : ""}
                            </div>
                          </div>
                        ) : i.status === "brouillon"
                          ? <Badge className="bg-[#FFDCA8] text-[#92400E] text-[10px] font-bold">Brouillon</Badge>
                          : isOverdue
                            ? <Badge className="bg-red-500 text-white text-[10px] font-bold">En retard</Badge>
                            : <Badge variant="outline" className="text-[10px]">À régler</Badge>}
                        {i.reminder_count > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setHistoryFor(i); }}
                            data-testid={`invoice-reminders-${i.id}`}
                            className="text-[10px] text-[#06B6D4] font-bold mt-0.5 hover:underline inline-flex items-center gap-0.5"
                          ><History size={9} />{i.reminder_count} relance(s)</button>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex gap-1">
                          {/* Voir l'aperçu */}
                          <button onClick={(e) => { e.stopPropagation(); setActive(i); }} title="Voir l'aperçu" data-testid={`invoice-view-${i.id}`} className="p-1.5 rounded hover:bg-black/5 text-[#0A1128]">
                            <Eye size={14} />
                          </button>
                          {/* Modifier (brouillon uniquement, non-CN) */}
                          {i.status === "brouillon" && !isCN && (
                            <button onClick={(e) => { e.stopPropagation(); setEditing(i); }} title="Modifier" data-testid={`invoice-edit-${i.id}`} className="p-1.5 rounded hover:bg-black/5 text-[#06B6D4]">
                              <Edit3 size={14} />
                            </button>
                          )}
                          {/* Envoi TEST (aperçu sur email perso) */}
                          {i.client_email && !isCN && (
                            <button onClick={(e) => { e.stopPropagation(); setSendModal({ inv: i, mode: i.status === "brouillon" ? "send" : "remind", preset: "test" }); }} title="Envoyer un APERÇU à un email de test" data-testid={`invoice-test-${i.id}`} className="p-1.5 rounded hover:bg-purple-50 text-purple-600">
                              <Beaker size={14} />
                            </button>
                          )}
                          {/* Envoyer au client (double confirm) */}
                          {i.status === "brouillon" && i.client_email && !isCN && (
                            <button onClick={(e) => { e.stopPropagation(); sendInvoice(i); }} title="Envoyer au client" data-testid={`invoice-send-${i.id}`} className="p-1.5 rounded hover:bg-green-50 text-green-600">
                              <Send size={14} />
                            </button>
                          )}
                          {/* Relancer */}
                          {!i.paid && i.status !== "brouillon" && i.client_email && !isCN && (
                            <button onClick={(e) => { e.stopPropagation(); sendReminder(i); }} disabled={reminding === i.id} title="Relancer" data-testid={`invoice-remind-${i.id}`} className={`p-1.5 rounded transition-colors ${isOverdue ? "text-red-600 hover:bg-red-50 animate-pulse" : "text-[#FFDCA8] hover:bg-[#FFDCA8]/20"}`}>
                              <BellRing size={14} />
                            </button>
                          )}
                          {!isCN && (
                            <button onClick={(e) => { e.stopPropagation(); setCreditNoteFor(i); }} title="Créer un avoir" data-testid={`invoice-avoir-${i.id}`} className="p-1.5 rounded hover:bg-orange-50 text-orange-600">
                              <FileMinus2 size={14} />
                            </button>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); dl(i.id, i.ref); }} title="Télécharger PDF" data-testid={`invoice-pdf-${i.id}`} className="p-1.5 rounded hover:bg-black/5 text-[#06B6D4]"><Download size={13} /></button>
                        </div>
                        {/* Indicateur d'ouverture par le client */}
                        {i.last_opened_at && (
                          <div className="text-[10px] mt-1 font-bold text-[#065F46] inline-flex items-center gap-1" title={`Ouvert ${new Date(i.last_opened_at).toLocaleString("fr-FR")}${i.open_count > 1 ? ` (${i.open_count}×)` : ""}`}>
                            <MailOpen size={10} />Lu{i.open_count > 1 ? ` ${i.open_count}×` : ""}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white border border-black/8 rounded-2xl overflow-hidden lg:sticky lg:top-4 lg:max-h-[calc(100vh-340px)]">
          {active ? (
            <>
              <div className="px-4 py-3 border-b border-black/8 bg-[#FAFAFA] flex items-center justify-between flex-wrap gap-2">
                <div className="min-w-0">
                  <div className="display font-extrabold text-[#0A1128] text-sm truncate">Aperçu · {active.ref}</div>
                  <div className="text-[11px] text-[#64748B] truncate">{active.client_email}{active.proposal_ref ? ` · ${active.proposal_ref}` : ""}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => dl(active.id, active.ref)} className="inline-flex items-center gap-1 text-xs font-bold text-white bg-[#0A1128] hover:bg-[#14213d] px-2.5 py-1.5 rounded-md" data-testid="invoice-download-pdf"><Download size={12} />PDF</button>
                  {previewUrl && <a href={previewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#06B6D4] hover:underline px-1"><ExternalLink size={12} /></a>}
                </div>
              </div>
              {previewUrl ? (
                <object data={`${previewUrl}#toolbar=0&navpanes=0`} type="application/pdf" className="w-full" style={{ height: "calc(100vh - 540px)", minHeight: 360 }}>
                  <iframe title={`PDF ${active.ref}`} src={previewUrl} className="w-full" style={{ height: "calc(100vh - 540px)", minHeight: 360 }} />
                </object>
              ) : (
                <div className="p-10 text-center text-sm text-[#64748B]">Chargement de l'aperçu…</div>
              )}
              <EmailHistoryPanel docType="invoice" docId={active.id} docRef={active.ref} />
            </>
          ) : (
            <div className="p-10 text-center text-sm text-[#64748B]">Sélectionnez une facture.</div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreate && <CreateInvoiceModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {showAcompte && <AcompteModal onClose={() => setShowAcompte(false)} onCreated={() => { setShowAcompte(false); load(); }} />}
      {editing && <EditInvoiceModal invoice={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {creditNoteFor && <CreditNoteModal invoice={creditNoteFor} onClose={() => setCreditNoteFor(null)} onCreated={() => { setCreditNoteFor(null); load(); }} />}
      {historyFor && <RemindersHistoryModal invoice={historyFor} onClose={() => setHistoryFor(null)} />}
      {sendModal && (
        <InvoiceSendModal
          inv={sendModal.inv}
          mode={sendModal.mode}
          preset={sendModal.preset}
          onClose={() => setSendModal(null)}
          onDone={() => { setSendModal(null); load(); }}
        />
      )}
    </div>
  );
}

function InvoiceSendModal({ inv, mode, preset, onClose, onDone }) {
  const [testEmail, setTestEmail] = useState(preset === "test" ? "" : "");
  const [recipient, setRecipient] = useState(inv.client_email || "");
  const [confirm, setConfirm] = useState(false);  // double confirm before sending to client
  const [busy, setBusy] = useState(false);
  const endpoint = mode === "send" ? `/admin/invoices/${inv.id}/send` : `/admin/invoices/${inv.id}/remind`;
  const title = mode === "send" ? `Envoyer la facture ${inv.ref}` : `Relancer la facture ${inv.ref}`;
  const cta = mode === "send" ? "Envoyer au client" : "Relancer le client";

  const sendTest = async () => {
    const t = testEmail.trim();
    if (!t) return toast.error("Renseignez un email de test");
    setBusy(true);
    try {
      const r = await api.post(endpoint, { test_email: t });
      toast.success(r.data.email_sent ? `Aperçu envoyé à ${t} ✉️` : "Aperçu non envoyé (vérifiez la config)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const sendReal = async () => {
    const r2 = recipient.trim();
    if (!r2 || !r2.includes("@")) return toast.error("Destinataire invalide");
    if (!confirm) { setConfirm(true); return; }
    setBusy(true);
    try {
      const r = await api.post(endpoint, { to: r2 });
      if (mode === "send") toast.success(r.data.email_sent ? `Facture envoyée à ${r2} ✉️` : "Statut mis à jour (email non configuré)");
      else toast.success(r.data.email_sent ? `Relance #${r.data.reminder_count} envoyée à ${r2} ✉️` : "Relance non envoyée (vérifiez la config)");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#0A1128]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="invoice-send-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-black/8 flex items-center justify-between">
          <div className="display font-extrabold text-[#0A1128]">{title}</div>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#0A1128]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Editable recipient */}
          <div>
            <Label className="text-xs text-[#0A1128]">Destinataire (modifiable)</Label>
            <Input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="client@exemple.fr"
              data-testid="invoice-recipient-input"
              className="text-sm"
            />
            <p className="text-[10px] text-[#94a3b8] mt-1">Par défaut : <b>{inv.client_email}</b> (issu de la fiche client). Modifiez pour envoyer à une autre adresse sans toucher la fiche.</p>
          </div>

          {/* Test */}
          <div className="bg-purple-50/60 border border-purple-200 rounded-xl p-3">
            <Label className="flex items-center gap-1.5 text-[#0A1128] text-xs mb-1"><Beaker size={12} className="text-purple-500" />Envoi APERÇU à un email de test (le client ne reçoit rien)</Label>
            <div className="flex gap-2">
              <Input type="email" placeholder="ex : moi@exemple.fr" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="invoice-test-email-input" />
              <Button onClick={sendTest} disabled={busy || !testEmail.trim()} variant="outline" data-testid="invoice-send-test-btn" className="border-purple-300 text-purple-700 hover:bg-purple-100">
                {busy ? <Loader2 className="animate-spin" size={14} /> : "Envoyer test"}
              </Button>
            </div>
          </div>

          {/* Double confirm */}
          <div className={`rounded-xl p-3 border ${confirm ? "bg-red-50 border-red-300" : "bg-[#FAFAFA] border-black/10"}`}>
            <div className="text-xs text-[#0A1128]">
              {confirm
                ? <><b>⚠ Double confirmation requise.</b> Cet email partira directement à <b>{recipient}</b>. Confirme-tu l'envoi définitif ?</>
                : <>L'email sera adressé à <b>{recipient}</b> et son statut sera mis à jour.</>}
            </div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-black/8 bg-[#FAFAFA] flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="invoice-send-cancel">Annuler</Button>
          {confirm && <Button variant="outline" onClick={() => setConfirm(false)} disabled={busy}>Revenir</Button>}
          <Button onClick={sendReal} disabled={busy} className={confirm ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#0A1128] hover:bg-[#14213d] text-white"} data-testid="invoice-send-confirm">
            {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <Send size={14} className="mr-1" />}
            {confirm ? "Confirmer l'envoi" : cta}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreateInvoiceModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ client_name: "", client_email: "", company_name: "", label: "Prestation", amount_ht: 0, vat_rate: 20, due_at: "", notes: "" });
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [showAuto, setShowAuto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendWelcome, setSendWelcome] = useState(true);

  useEffect(() => {
    api.get("/admin/entrepreneurs").then((r) => {
      const list = [];
      (r.data || []).forEach((e) => {
        (e.companies || []).forEach((c) => list.push({
          name: e.full_name || e.email,
          email: c.email || e.email,
          company: c.name,
        }));
      });
      setClients(list);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!search) return clients.slice(0, 5);
    const q = search.toLowerCase();
    return clients.filter((c) => (c.name || "").toLowerCase().includes(q) || (c.company || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q)).slice(0, 8);
  }, [clients, search]);

  const submit = async () => {
    if (!form.client_name) return toast.error("Nom du client requis");
    if (!form.amount_ht || form.amount_ht <= 0) return toast.error("Montant HT requis");
    setBusy(true);
    try {
      const r = await api.post("/admin/invoices", { ...form, send_welcome_email: sendWelcome });
      const w = r.data?.welcome_email;
      if (w?.sent) toast.success("Facture créée · email de bienvenue envoyé ✉️");
      else if (w && !w.sent) toast.success(`Facture créée — Email de bienvenue : ${w.error || "non envoyé"}`);
      else toast.success("Facture créée");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const ttc = (Number(form.amount_ht) || 0) * (1 + (Number(form.vat_rate) || 0) / 100);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Nouvelle facture</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Label>Client (recherche ou saisie libre)</Label>
            <Input
              data-testid="new-invoice-client-search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setForm({ ...form, client_name: e.target.value }); setShowAuto(true); }}
              onFocus={() => setShowAuto(true)}
              placeholder="Tapez le nom du client…"
            />
            {showAuto && filtered.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-black/10 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {filtered.map((c, i) => (
                  <button key={i} onClick={() => { setForm({ ...form, client_name: c.name, client_email: c.email || "", company_name: c.company || "" }); setSearch(c.name); setShowAuto(false); }} className="w-full text-left px-3 py-2 hover:bg-[#FAFAFA] text-sm border-b border-black/5 last:border-0">
                    <div className="font-medium text-[#0A1128]">{c.name}</div>
                    <div className="text-xs text-[#64748B]">{c.company} · {c.email}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Email client</Label><Input value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="contact@..." /></div>
            <div><Label>Entreprise</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></div>
          </div>
          <div><Label>Libellé prestation</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Montant HT (€)</Label><Input type="number" step="0.01" value={form.amount_ht} onChange={(e) => setForm({ ...form, amount_ht: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>TVA (%)</Label><Input type="number" step="0.1" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Échéance</Label><Input type="date" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} /></div>
          </div>
          <div className="bg-[#FAFAFA] border border-black/8 rounded-xl p-3 text-sm">
            <div className="flex justify-between"><span>Total HT</span><span className="font-bold tabular-nums">{(Number(form.amount_ht) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</span></div>
            <div className="flex justify-between text-[#64748B]"><span>TVA {form.vat_rate}%</span><span className="tabular-nums">{((Number(form.amount_ht) || 0) * (Number(form.vat_rate) / 100)).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</span></div>
            <div className="flex justify-between text-[#06B6D4] font-extrabold mt-1 pt-1 border-t border-black/10"><span>Total TTC</span><span className="tabular-nums">{ttc.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</span></div>
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <label className="flex items-start gap-2.5 p-3 rounded-xl bg-[#06B6D4]/8 border border-[#06B6D4]/30 cursor-pointer" data-testid="new-invoice-send-welcome-wrap">
            <input
              type="checkbox"
              checked={sendWelcome}
              onChange={(e) => setSendWelcome(e.target.checked)}
              data-testid="new-invoice-send-welcome"
              className="mt-0.5 w-4 h-4 accent-[#06B6D4] cursor-pointer"
            />
            <span className="text-xs leading-snug text-[#0A1128]">
              <span className="font-bold">Envoyer un email de bienvenue au client</span> — il recevra un lien (valide 7 jours) pour activer son Espace Entreprise.
              <span className="block text-[10px] text-[#64748B] mt-0.5 italic">Ignoré si l'email est vide ou si le client a déjà un compte.</span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#06B6D4] text-[#0A1128] font-bold" data-testid="new-invoice-submit">{busy ? "Création…" : "Créer la facture"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditInvoiceModal({ invoice, onClose, onSaved }) {
  const [f, setF] = useState({ ...invoice });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        label: f.label, amount_ht: f.amount_ht, vat_rate: f.vat_rate, due_at: f.due_at, notes: f.notes,
        status: f.status, paid: f.paid, paid_at: f.paid_at, paid_method: f.paid_method, paid_amount: f.paid_amount,
      };
      await api.patch(`/admin/invoices/${invoice.id}`, payload);
      toast.success("Facture mise à jour");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Modifier la facture {invoice.ref}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Libellé</Label><Input value={f.label || ""} onChange={(e) => setF({ ...f, label: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>HT (€)</Label><Input type="number" step="0.01" value={f.amount_ht || 0} onChange={(e) => setF({ ...f, amount_ht: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>TVA (%)</Label><Input type="number" step="0.1" value={f.vat_rate ?? 20} onChange={(e) => setF({ ...f, vat_rate: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Échéance</Label><Input type="date" value={(f.due_at || "").slice(0, 10)} onChange={(e) => setF({ ...f, due_at: e.target.value })} /></div>
          </div>
          <div className="border-t border-black/8 pt-3">
            <div className="flex items-center gap-2 mb-2">
              <input id="paid" type="checkbox" checked={!!f.paid} onChange={(e) => setF({ ...f, paid: e.target.checked })} data-testid="edit-paid-toggle" />
              <Label htmlFor="paid" className="cursor-pointer font-bold text-green-700 inline-flex items-center gap-1"><CreditCard size={14} />Marquer comme payée</Label>
            </div>
            {f.paid && (
              <div className="grid grid-cols-3 gap-3 bg-green-50 p-3 rounded-xl">
                <div>
                  <Label>Montant reçu (€)</Label>
                  <Input type="number" step="0.01" value={f.paid_amount || f.ttc || 0} onChange={(e) => setF({ ...f, paid_amount: parseFloat(e.target.value) || 0 })} data-testid="edit-paid-amount" />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={(f.paid_at || "").slice(0, 10)} onChange={(e) => setF({ ...f, paid_at: e.target.value })} data-testid="edit-paid-date" />
                </div>
                <div>
                  <Label>Type de règlement</Label>
                  <select className="w-full h-9 border border-input rounded-md px-2 text-sm" value={f.paid_method || ""} onChange={(e) => setF({ ...f, paid_method: e.target.value })} data-testid="edit-paid-method">
                    <option value="">—</option>
                    {PAID_METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={busy} className="bg-[#0A1128] text-white" data-testid="edit-invoice-save">{busy ? "Sauvegarde…" : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreditNoteModal({ invoice, onClose, onCreated }) {
  const [mode, setMode] = useState("partial");
  const [amount, setAmount] = useState(invoice.amount_ht || 0);
  const [reason, setReason] = useState("Avoir commercial");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/invoices/${invoice.id}/credit-note`, {
        reason,
        amount_ht: mode === "total" ? null : Number(amount),
      });
      toast.success("Avoir créé");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Créer un avoir sur {invoice.ref}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="bg-[#FAFAFA] border border-black/8 rounded-xl p-3 text-sm">
            <div className="text-[#64748B]">Facture initiale</div>
            <div className="font-bold text-[#0A1128]">{invoice.label} — {Number(invoice.amount_ht).toLocaleString("fr-FR")} € HT</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setMode("partial")} className={`flex-1 py-2 px-3 rounded-lg border text-sm font-bold ${mode === "partial" ? "border-[#06B6D4] bg-[#06B6D4]/10 text-[#06B6D4]" : "border-black/10 text-[#64748B]"}`} data-testid="avoir-mode-partial">Avoir partiel</button>
            <button onClick={() => setMode("total")} className={`flex-1 py-2 px-3 rounded-lg border text-sm font-bold ${mode === "total" ? "border-red-500 bg-red-50 text-red-600" : "border-black/10 text-[#64748B]"}`} data-testid="avoir-mode-total">Avoir total</button>
          </div>
          {mode === "partial" && (
            <div><Label>Montant HT à créditer (€)</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} max={invoice.amount_ht} data-testid="avoir-amount" /></div>
          )}
          <div><Label>Motif</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="avoir-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={busy} className="bg-orange-600 text-white" data-testid="avoir-submit">{busy ? "Création…" : "Créer l'avoir"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RemindersHistoryModal({ invoice, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/invoices/${invoice.id}/reminders`).then((r) => setData(r.data)).catch(() => setData({ reminders: [], count: 0 }));
  }, [invoice.id]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Historique des relances — {invoice.ref}</DialogTitle></DialogHeader>
        {!data ? <div className="text-sm text-[#64748B]">Chargement…</div> : (
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="reminders-history">
            {(data.reminders || []).length === 0 ? (
              <div className="text-sm text-[#64748B] text-center py-6">Aucune relance dans l'historique enrichi (les anciennes relances ne sont pas tracées).<br/><span className="text-[#06B6D4] font-bold">Total : {data.count} relance(s)</span></div>
            ) : (
              <ul className="space-y-2">
                {(data.reminders || []).slice().reverse().map((r, i) => (
                  <li key={r.id || i} className="bg-[#FAFAFA] border border-black/8 rounded-xl p-3 text-sm">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="font-bold text-[#0A1128]">{fmtShort(r.at)} {new Date(r.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                      <Badge className={r.sent ? "bg-green-600" : "bg-[#FFDCA8] text-[#92400E]"}>{r.sent ? "Envoyée ✉️" : "Non envoyée"}</Badge>
                    </div>
                    <div className="text-xs text-[#64748B] mt-1">Canal : {r.channel} · vers {r.to}</div>
                    <div className="text-xs text-[#0A1128] mt-1">{r.subject}</div>
                    {r.overdue_days > 0 && <div className="text-[10px] text-red-600 font-bold mt-1">Retard : +{r.overdue_days} j</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <DialogFooter><Button onClick={onClose}>Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ tone, icon: Icon, label, value, sub }) {
  const tones = {
    navy:  { bg: "bg-[#0A1128]", fg: "text-white",         iconFg: "text-[#67E8F9]" },
    red:   { bg: "bg-red-500",   fg: "text-white",         iconFg: "text-white"     },
    green: { bg: "bg-[#A7F3D0]", fg: "text-[#065F46]",     iconFg: "text-[#065F46]" },
    cyan:  { bg: "bg-[#06B6D4]", fg: "text-white",         iconFg: "text-white"     },
  }[tone] || { bg: "bg-white", fg: "text-[#0A1128]", iconFg: "text-[#06B6D4]" };
  return (
    <div className={`${tones.bg} ${tones.fg} rounded-2xl p-4 shadow-sm relative overflow-hidden`}>
      <div className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
        <Icon size={16} className={tones.iconFg} />
      </div>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="display text-2xl sm:text-3xl font-extrabold mt-1.5 tabular-nums">{value}</div>
      <div className="text-[11px] mt-0.5 opacity-80">{sub}</div>
    </div>
  );
}

function AcompteModal({ onClose, onCreated }) {
  const [proposals, setProposals] = useState([]);
  const [selected, setSelected] = useState(null);   // proposal object {id, ref, client, totals…}
  const [project, setProject] = useState(null);     // matching project (commande) — null if not yet auto-created
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("percent");      // percent | amount | balance
  const [value, setValue] = useState(50);
  const [label, setLabel] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("virement");
  const [dueAt, setDueAt] = useState("");
  const [installmentIdx, setInstallmentIdx] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Only show signed devis (the user wants intermediate invoices on accepted proposals)
    api.get("/admin/proposals?status=signe").then((r) => setProposals(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) { setProject(null); return; }
    api.get(`/admin/projects?source_proposal_id=${selected.id}`).then((r) => {
      const list = r.data || [];
      const p = list.find((x) => x.source_proposal_id === selected.id) || list[0] || null;
      setProject(p);
    }).catch(() => setProject(null));
  }, [selected]);

  const filtered = useMemo(() => {
    if (!search) return proposals;
    const s = search.toLowerCase();
    return proposals.filter((p) => (p.ref || "").toLowerCase().includes(s) || (p.client?.name || "").toLowerCase().includes(s) || (p.client?.company || "").toLowerCase().includes(s));
  }, [proposals, search]);

  const totalTtc = Number(selected?.totals?.total_ttc || 0);
  const alreadyInvoiced = Number(project?.total_invoiced || 0);
  const remainingBefore = Math.max(0, totalTtc - alreadyInvoiced);

  const computedAmount = useMemo(() => {
    if (!selected) return 0;
    if (kind === "balance") return remainingBefore;
    const v = parseFloat(value) || 0;
    if (kind === "percent") return Math.round(totalTtc * Math.min(100, v) / 100 * 100) / 100;
    return Math.min(v, remainingBefore);
  }, [kind, value, totalTtc, remainingBefore, selected]);

  const submit = async () => {
    if (!selected) return toast.error("Sélectionnez un devis");
    if (kind !== "balance" && (!value || value <= 0)) return toast.error("Montant invalide");
    setBusy(true);
    try {
      await api.post("/admin/invoices/from-proposal", {
        proposal_id: selected.id,
        project_id: project?.id,
        kind,
        value: kind === "balance" ? null : parseFloat(value),
        label: label || null,
        payment_method: paymentMethod,
        due_at: dueAt || null,
        installment_index: installmentIdx,
      });
      toast.success("Facture d'acompte créée");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="acompte-modal">
        <DialogHeader><DialogTitle>Facture d&apos;acompte sur devis</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[68vh] overflow-y-auto pr-1">
          {/* Selector */}
          {!selected ? (
            <>
              <div><Label>Rechercher un devis signé</Label>
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Référence ou nom client" data-testid="acompte-search" />
              </div>
              <div className="border border-black/8 rounded-xl divide-y divide-black/5 max-h-64 overflow-y-auto" data-testid="acompte-list">
                {filtered.length === 0 ? <div className="px-3 py-3 text-sm text-[#64748B] italic">Aucun devis signé trouvé.</div> :
                  filtered.map((p) => (
                    <button key={p.id} type="button" onClick={() => setSelected(p)} className="w-full text-left px-3 py-2.5 hover:bg-[#06B6D4]/8 transition-colors" data-testid={`acompte-pick-${p.id}`}>
                      <div className="font-bold text-[#0A1128] text-sm">{p.ref} — {p.client?.name || "?"}</div>
                      <div className="text-[11px] text-[#64748B]">{p.client?.company || ""} · {fmtEur(p.totals?.total_ttc || 0)} TTC</div>
                    </button>
                  ))}
              </div>
            </>
          ) : (
            <>
              <div className="bg-[#FAFAFA] border border-black/8 rounded-xl p-3 flex items-start justify-between gap-2" data-testid="acompte-selected">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#06B6D4]">Devis sélectionné</div>
                  <div className="font-bold text-[#0A1128]">{selected.ref} — {selected.client?.name}</div>
                  <div className="text-xs text-[#64748B]">{selected.client?.company} · Total {fmtEur(totalTtc)} TTC</div>
                  {project?.order_ref && <div className="text-xs text-[#06B6D4] font-bold mt-0.5">Commande {project.order_ref}</div>}
                </div>
                <button type="button" onClick={() => setSelected(null)} className="text-[11px] text-red-600 hover:underline font-bold">Changer</button>
              </div>

              {/* Order recap */}
              <div className="grid grid-cols-3 gap-2 text-center" data-testid="acompte-recap">
                <div className="bg-white border border-black/8 rounded-lg p-2">
                  <div className="text-[9px] uppercase tracking-widest text-[#64748B] font-bold">Total commande</div>
                  <div className="font-extrabold text-[#0A1128] text-sm">{fmtEur(totalTtc)}</div>
                </div>
                <div className="bg-white border border-black/8 rounded-lg p-2">
                  <div className="text-[9px] uppercase tracking-widest text-[#64748B] font-bold">Déjà facturé</div>
                  <div className="font-extrabold text-amber-700 text-sm">{fmtEur(alreadyInvoiced)}</div>
                </div>
                <div className="bg-white border border-black/8 rounded-lg p-2">
                  <div className="text-[9px] uppercase tracking-widest text-[#64748B] font-bold">Reste à facturer</div>
                  <div className="font-extrabold text-green-700 text-sm">{fmtEur(remainingBefore)}</div>
                </div>
              </div>

              {/* Installments (if any) */}
              {project?.installments?.length > 0 && (
                <div data-testid="acompte-installments">
                  <Label>Échéance liée (optionnel)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {project.installments.map((it) => (
                      <button key={it.index} type="button" onClick={() => { setInstallmentIdx(it.index); setKind("amount"); setValue(it.amount); setLabel(it.label); }}
                        className={`text-left px-3 py-2 rounded-lg border text-xs transition-colors ${installmentIdx === it.index ? "bg-[#06B6D4]/15 border-[#06B6D4]" : "bg-white border-black/10 hover:border-[#06B6D4]/40"} ${it.status === "paid" ? "opacity-50" : ""}`}
                        disabled={it.status === "paid"}>
                        <div className="font-bold text-[#0A1128]">{it.label}</div>
                        <div className="text-[10px] text-[#64748B]">{fmtEur(it.amount)} · {fmtShort(it.due_at)} · {it.status}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Amount controls */}
              <div className="space-y-2">
                <Label>Montant facturé</Label>
                <div className="flex gap-2">
                  <select value={kind} onChange={(e) => setKind(e.target.value)} className="border border-black/10 rounded-md px-2 py-2 text-sm font-bold" data-testid="acompte-kind">
                    <option value="percent">% du devis</option>
                    <option value="amount">Montant €</option>
                    <option value="balance">Solde restant</option>
                  </select>
                  {kind !== "balance" && (
                    <Input type="number" step="0.01" min="0" value={value} onChange={(e) => setValue(e.target.value)} placeholder={kind === "percent" ? "50" : "1500"} data-testid="acompte-value" />
                  )}
                </div>
                <div className="text-xs text-[#06B6D4] font-bold tabular-nums">Montant calculé : {fmtEur(computedAmount)} TTC</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label>Moyen de paiement attendu</Label>
                  <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full border border-black/10 rounded-md px-2 py-2 text-sm" data-testid="acompte-method">
                    {PAID_METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select>
                </div>
                <div><Label>Échéance</Label><Input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} data-testid="acompte-due" /></div>
              </div>

              <div><Label>Libellé (optionnel)</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`Acompte ${kind === "percent" ? value + "%" : ""} sur devis ${selected.ref}`} data-testid="acompte-label" /></div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={!selected || busy} className="bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-bold" data-testid="acompte-submit">
            {busy ? "Création…" : "Créer la facture"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

